import GeoJSON from 'ol/format/GeoJSON';
import { getDistance } from 'ol/sphere';
import { Stroke, Style, Fill } from 'ol/style';
import {
  addFeaturesToLayer,
  clearMapLayer,
  getAllLayerFeatures,
} from 'utils/map/layers';
import { APPROVED_AREAS_STYLE } from 'consts/mapConfig';
import { convertFeetToMeters } from 'utils/main';

const areaStyle = new Style({
  stroke: new Stroke(APPROVED_AREAS_STYLE.stroke),
  fill: new Fill(APPROVED_AREAS_STYLE.fill),
});

export const areaSelectedStyle = new Style({
  stroke: new Stroke(APPROVED_AREAS_STYLE.selected.stroke),
  fill: new Fill(APPROVED_AREAS_STYLE.selected.fill),
});

export const addApprovedAreasToLayer = (layer, approvedAreas = []) => {
  if (!layer) return;
  clearMapLayer(layer);
  const allFeatures = [];
  for (const area of approvedAreas) {
    const parser = new GeoJSON();
    const features = parser.readFeatures(area.geojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });

    features.forEach((feature, index) => {
      feature.setId(`${area.uid}-#${index}`);
      feature.setProperties({
        uid: area.uid,
        name: area.name,
        description: area.description,
        width: area.width,
        min: area.min,
        max: area.max,
        detail: 'approvedArea',
      });
      feature.setStyle(areaStyle);
      allFeatures.push(feature);
    });
  }
  addFeaturesToLayer(layer, allFeatures);
};

export const removeLineStringFromApprovedArea = (area = {}) => {
  let tmpArea = area?.geojson.features;
  tmpArea = tmpArea.filter(item => item.geometry.type !== 'LineString');
  return tmpArea;
};

export const serializeData = (
  xArrayValues = [],
  yArrayValues = [],
  chartDrawCase
) => {
  if (xArrayValues.length === yArrayValues.length) {
    const serializedData = [];
    for (let i = 0; i < xArrayValues.length; i += 1) {
      const eachChartPoint = {
        x: xArrayValues[i]?.toFixed(4),
        y: yArrayValues[i]?.toFixed(4),
      };
      serializedData.push(eachChartPoint);
      if (i !== xArrayValues.length - 1 && chartDrawCase === true) {
        // if we add one more point on the same Y level but with shifted X value we are getting stepped type of chart
        const extraChartSteppedPoint = {
          x: xArrayValues[i + 1].toFixed(4),
          y: yArrayValues[i].toFixed(4),
        };
        serializedData.push(extraChartSteppedPoint);
      }
    }
    return serializedData;
  }
};

export const returnLineLevelFromApprovedArea = (
  approvedArea,
  height,
  heightUnitConversion
) => {
  const data = [];
  for (const partOfApprovedArea of approvedArea) {
    const elevation = !heightUnitConversion
      ? convertFeetToMeters(
          parseFloat(partOfApprovedArea.properties.elevation),
          true
        )
      : parseFloat(partOfApprovedArea.properties.elevation);
    const tempHeight = !heightUnitConversion
      ? convertFeetToMeters(parseFloat(height), true)
      : parseFloat(height);
    data.push(elevation + tempHeight);
  }
  // Adding last elem twice, because variable elevationAvgM is set for coordinates[0][0] to coordinates[0][1] and we are missing height for last part of approvedArea
  data.push(data[data.length - 1]);

  return data;
};

export const getApprovedAreaPartsLength = (cleanedAreas = []) => {
  const approvedAreaPartLengths = [0];
  let tmpDist = 0;
  for (const partOfApprovedArea of cleanedAreas) {
    const partLength = getDistance(
      partOfApprovedArea.geometry.coordinates[0][0],
      partOfApprovedArea.geometry.coordinates[0][1]
    ).toFixed(2);
    tmpDist += parseFloat(partLength, 10);
    // function above getDistance returns meters, below divide to kilometers
    approvedAreaPartLengths.push(tmpDist / 1000);
  }

  return approvedAreaPartLengths;
};

export const setSelectedAreaStyle = (layer, featureUid) => {
  const features = getAllLayerFeatures(layer);
  if (features && features.length)
    features.forEach(f => {
      if (f.getProperties()?.uid === featureUid) f.setStyle(areaSelectedStyle);
    });
};

export const setAreasBasicStyle = layer => {
  const features = getAllLayerFeatures(layer);
  if (features) features.forEach(f => f.setStyle(areaStyle));
};

export const changeObjectHeightUnit = (zones, heightUnitConversion) =>
  zones.map(zone => {
    if (!heightUnitConversion) {
      zone.agl_min = `${convertFeetToMeters(zone.agl_min, true)}`;
      zone.agl_max = `${convertFeetToMeters(zone.agl_max, true)}`;
      return zone;
    }
    zone.agl_min = `${zone.agl_min}`;
    zone.agl_max = `${zone.agl_max}`;
    return zone;
  });
